import { useEffect, useRef, useState } from "react";
import { api, errMsg, type Realm } from "../api";
import { Btn, Field, Input, Notice } from "./ui";

interface Start { secret: string; otpauthUri: string; qrDataUrl: string }

/** Inscrição de 2FA (TOTP). `challenge` = fluxo obrigatório do login administrativo; sem ele usa a sessão (cliente). */
export function TwoFactorEnroll({ realm, challenge, onFinished }: { realm: Realm; challenge?: string; onFinished: () => void }) {
  const [start, setStart] = useState<Start | null>(null);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState<string[] | null>(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    api<Start>("POST", `/api/${realm}/auth/mfa/enroll/start`, { challenge }).then(setStart).catch((e) => setErr(errMsg(e)));
  }, [realm, challenge]);

  async function confirm() {
    setBusy(true); setErr("");
    try {
      const r = await api<{ recoveryCodes: string[] }>("POST", `/api/${realm}/auth/mfa/enroll/confirm`, { challenge, code: code.replace(/\s/g, "") });
      setCodes(r.recoveryCodes);
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  }

  if (codes) {
    return (
      <div>
        <Notice kind="ok">Autenticação em dois fatores ativada.</Notice>
        <p className="muted" style={{ marginTop: 0 }}>Guarde estes códigos de recuperação em local seguro. Cada um funciona uma única vez e não será exibido novamente.</p>
        <div className="codes" data-testid="recovery-codes">{codes.map((c) => <span key={c}>{c}</span>)}</div>
        <div style={{ marginTop: 14 }}><Btn onClick={onFinished}>Já guardei os códigos</Btn></div>
      </div>
    );
  }
  return (
    <div>
      {err && <Notice kind="error">{err}</Notice>}
      {!start && !err && <p className="muted">Preparando…</p>}
      {start && (
        <>
          <p style={{ marginTop: 0 }}>Escaneie o QR code no seu aplicativo autenticador (Google Authenticator, Authy, 1Password…) e informe o código de 6 dígitos.</p>
          <div className="qr"><img src={start.qrDataUrl} width={180} height={180} alt="QR code para o aplicativo autenticador" /></div>
          <p className="muted" style={{ fontSize: 12.5 }}>Sem câmera? Digite a chave manualmente: <span className="tabular" data-testid="totp-secret">{start.secret}</span></p>
          <Field label="Código de 6 dígitos"><Input inputMode="numeric" autoComplete="one-time-code" maxLength={7} value={code} onChange={(e) => setCode(e.target.value)} /></Field>
          <Btn onClick={confirm} disabled={busy || code.replace(/\s/g, "").length !== 6}>Ativar 2FA</Btn>
        </>
      )}
    </div>
  );
}
